import React, { useMemo, useCallback } from 'react';
import { ReactGrid } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import {
  createTextCell,
  createNumberCell,
  createDateCell,
  createDropdownCell,
  createCheckboxCell,
} from 'components/spreadsheet/cellType';

const defaultHeaderStyle = {
  background: '#f7f7f7',
  fontWeight: 600,
  textAlign: 'center',
  borderBottom: '1px solid #ccc',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const ExcelView = ({
  data = [],
  columns = [],
  onCellsChanged = () => {},
  className = 'excel-view-container position-relative',
  options = {},
}) => {
  const {
    enableGroupedHeader = false,
    headerLevels = 2,
    groupLabelKey = 'groupLabel',
    getHeaderStyle = () => defaultHeaderStyle,
    getColumnWidth = () => 120,
    stickyTopRows = headerLevels,
    mergeStrategy = {
      verticalMergeNonGrouped: true,
      horizontalMergeGrouped: true,
    },
  } = options;

  // 상단 헤더 생성 (Top Header) - 변경 없음
  const topHeader = useMemo(() => {
    if (!enableGroupedHeader || headerLevels < 2) return null;

    const headerCells = [];
    const processedGroups = new Set();
    const isHorizontalMerge = mergeStrategy.horizontalMergeGrouped;
    const isVerticalMerge = mergeStrategy.verticalMergeNonGrouped;

    columns.forEach(col => {
      const group = col[groupLabelKey];
      const isGrouped = !!group;

      if (!isGrouped) {
        headerCells.push({
          type: 'header',
          text: col.header || '',
          rowspan: isVerticalMerge ? 2 : 1,
          style: getHeaderStyle(col, 1),
        });
        return;
      }

      if (isHorizontalMerge) {
        if (!processedGroups.has(group)) {
          const groupCols = columns.filter(c => c[groupLabelKey] === group);

          headerCells.push({
            type: 'header',
            text: group,
            colspan: groupCols.length,
            style: getHeaderStyle(col, 1),
          });

          processedGroups.add(group);
        } else {
          headerCells.push({
            type: 'header',
            text: '',
            style: getHeaderStyle(col, 1),
          });
        }
      } else {
        headerCells.push({
          type: 'header',
          text: col.header || '',
          style: getHeaderStyle(col, 1),
        });
      }
    });

    return { rowId: 'top-header', cells: headerCells };
  }, [
    columns,
    enableGroupedHeader,
    headerLevels,
    groupLabelKey,
    getHeaderStyle,
    mergeStrategy,
  ]);

  // 하단 헤더 생성 (Sub Header) - 변경 없음
  const subHeader = useMemo(() => {
    const headerCells = [];
    const isVerticalMerge = mergeStrategy.verticalMergeNonGrouped;

    columns.forEach(col => {
      const group = col[groupLabelKey];
      const isGrouped = !!group;

      if (!isGrouped && isVerticalMerge) {
        headerCells.push({
          type: 'header',
          text: '',
          style: getHeaderStyle(col, 2),
        });
        return;
      }

      headerCells.push({
        type: 'header',
        text: col.header || '',
        style: getHeaderStyle(col, 2),
      });
    });

    return { rowId: 'sub-header', cells: headerCells };
  }, [columns, getHeaderStyle, groupLabelKey, mergeStrategy]);

  // 헤더 병합 구조 통합 - 변경 없음
  const headerRows = useMemo(() => {
    return enableGroupedHeader
      ? [topHeader, subHeader].filter(Boolean)
      : [
          {
            rowId: 'header',
            cells: columns.map(col => ({
              type: 'header',
              text: col.header || '',
              style: getHeaderStyle(col, 1),
            })),
          },
        ];
  }, [enableGroupedHeader, topHeader, subHeader, columns, getHeaderStyle]);

  // grid column 정의 - 변경 없음
  const gridColumns = useMemo(() => {
    return columns.map(col => ({
      columnId: col.key,
      resizable: true,
    }));
  }, [columns]);

  // 데이터 행 (✨수정: 텍스트 셀을 편집 가능하게 설정)
  const rows = useMemo(() => {
    const dataRows = data.map((item, index) => ({
      rowId: item.id || `data-row-${index}`,
      cells: columns.map(col => {
        let value = item[col.key];
        const disabled =
          typeof col.disabled === 'function'
            ? col.disabled(item)
            : col.disabled || false;

        let cell;
        switch (col.type) {
          case 'checkbox':
            cell = createCheckboxCell(!!value, disabled);
            break;
          case 'date':
            cell = createDateCell(value, disabled);
            break;
          case 'number':
            cell = createNumberCell(value, disabled);
            break;
          case 'dropdown':
            cell = createDropdownCell(
              value,
              col.options,
              disabled,
              item[`${col.key}_isOpen`] || false,
            );
            break;
          default:
            // null 또는 undefined 값을 빈 문자열로 변환
            value = value === null || value === undefined ? '' : value;

            cell = createTextCell(value, disabled);

            // ✨ 핵심 수정: disabled가 false(편집 가능해야 할 때)일 때
            // nonEditable 속성을 명시적으로 false로 설정하여 편집을 허용합니다.
            // disabled가 true이면 nonEditable: true가 됩니다.
            cell = {
              ...cell,
              nonEditable: disabled,
            };
        }
        return { ...cell, columnId: col.key };
      }),
    }));

    return [...headerRows, ...dataRows];
  }, [data, columns, headerRows]);

  const handleChanges = useCallback(
    changes => {
      onCellsChanged(changes);
    },
    [onCellsChanged],
  );

  return (
    // 100% 너비 및 높이 적용
    <div className={className} style={{ width: '100%', height: '100%' }}>
      <ReactGrid
        rows={rows}
        columns={gridColumns}
        onCellsChanged={handleChanges}
        enableRangeSelection
        enableFillHandle
        enableColumnSelection
        stickyTopRows={stickyTopRows}
        style={{ width: '100%', height: '100%' }} // ReactGrid에 100% 스타일 적용
      />
    </div>
  );
};

export default ExcelView;
